import { useEffect, useRef, useCallback } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';

// Message types for binary protocol
const MSG_TYPE_DATA = 0x00;
const MSG_TYPE_RESIZE = 0x01;

interface XTerminalProps {
  sessionId: string;
  onDisconnect?: () => void;
  onConnect?: () => void;
}

export function XTerminal({ sessionId, onDisconnect, onConnect }: XTerminalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Send resize message to backend
  const sendResize = useCallback((cols: number, rows: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const buffer = new ArrayBuffer(5);
      const view = new DataView(buffer);
      view.setUint8(0, MSG_TYPE_RESIZE);
      view.setUint16(1, cols, false); // big-endian
      view.setUint16(3, rows, false); // big-endian
      wsRef.current.send(buffer);
    }
  }, []);

  // Handle terminal resize
  const handleResize = useCallback(() => {
    if (fitAddonRef.current && terminalRef.current) {
      try {
        fitAddonRef.current.fit();
        const { cols, rows } = terminalRef.current;
        sendResize(cols, rows);
      } catch (e) {
        // Ignore fit errors during initialization
      }
    }
  }, [sendResize]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Create terminal instance
    const terminal = new Terminal({
      cursorBlink: true,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      fontSize: 14,
      lineHeight: 1.2,
      theme: {
        background: '#1a1b26',
        foreground: '#c0caf5',
        cursor: '#c0caf5',
        cursorAccent: '#1a1b26',
        selectionBackground: '#33467c',
        black: '#15161e',
        red: '#f7768e',
        green: '#9ece6a',
        yellow: '#e0af68',
        blue: '#7aa2f7',
        magenta: '#bb9af7',
        cyan: '#7dcfff',
        white: '#a9b1d6',
        brightBlack: '#414868',
        brightRed: '#f7768e',
        brightGreen: '#9ece6a',
        brightYellow: '#e0af68',
        brightBlue: '#7aa2f7',
        brightMagenta: '#bb9af7',
        brightCyan: '#7dcfff',
        brightWhite: '#c0caf5',
      },
      scrollback: 10000,
      convertEol: true,
    });

    terminalRef.current = terminal;

    // Create and load addons
    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();
    fitAddonRef.current = fitAddon;

    terminal.loadAddon(fitAddon);
    terminal.loadAddon(webLinksAddon);

    // Open terminal in container
    terminal.open(containerRef.current);

    // Initial fit
    setTimeout(() => {
      fitAddon.fit();
    }, 0);

    // Setup WebSocket connection
    const token = localStorage.getItem('access_token');
    if (!token) {
      terminal.writeln('\r\n\x1b[31mError: No authentication token\x1b[0m');
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/api/v1/terminal/ws/${sessionId}?token=${token}`;

    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      onConnect?.();
      // Send initial size
      const { cols, rows } = terminal;
      sendResize(cols, rows);
    };

    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        // Binary data from PTY
        const decoder = new TextDecoder();
        terminal.write(decoder.decode(event.data));
      } else {
        // Text data (fallback)
        terminal.write(event.data);
      }
    };

    ws.onclose = () => {
      terminal.writeln('\r\n\x1b[33m--- Connection closed ---\x1b[0m');
      onDisconnect?.();
    };

    ws.onerror = () => {
      terminal.writeln('\r\n\x1b[31m--- Connection error ---\x1b[0m');
    };

    // Forward keyboard input to WebSocket
    terminal.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        // Create binary message: [MSG_TYPE_DATA][utf8 data]
        const encoder = new TextEncoder();
        const utf8Data = encoder.encode(data);
        const buffer = new ArrayBuffer(1 + utf8Data.length);
        const view = new Uint8Array(buffer);
        view[0] = MSG_TYPE_DATA;
        view.set(utf8Data, 1);
        ws.send(buffer);
      }
    });

    // Setup resize observer
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    resizeObserverRef.current = resizeObserver;
    resizeObserver.observe(containerRef.current);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      ws.close();
      terminal.dispose();
    };
  }, [sessionId, onConnect, onDisconnect, sendResize, handleResize]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        padding: '8px',
        backgroundColor: '#1a1b26',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    />
  );
}
